import React from "react";
import { motion } from "framer-motion";
import { useT } from "@/lib/i18n";
import { CheckCircle2, Circle, Sparkles } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

export default function Roadmap() {
    const { t } = useT();
    const phases = t("roadmap.phases") || [];
    return (
        <section
            id="roadmap"
            data-testid="sn-roadmap"
            className="bg-sn-paper-2 py-24 md:py-40"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.9, ease: EASE }}
                    className="flex items-baseline gap-4 mb-14 md:mb-20"
                >
                    <span className="font-mono-e text-[11px] uppercase tracking-[0.3em] text-sn-blue">
                        {t("roadmap.eyebrow")}
                    </span>
                    <span className="hairline flex-1" />
                </motion.div>

                <motion.h2
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.9, ease: EASE }}
                    className="font-serif text-5xl md:text-7xl leading-[0.98] tracking-tight max-w-3xl text-sn-ink"
                >
                    {t("roadmap.headline_a")}{" "}
                    <em className="italic text-sn-green">{t("roadmap.headline_b")}</em>.
                </motion.h2>

                <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
                    {phases.map((ph, i) => {
                        const live = i === 0;
                        return (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.25 }}
                                transition={{ duration: 0.8, delay: i * 0.12, ease: EASE }}
                                className={`rounded-md p-7 border ${live ? "bg-white border-sn-green/40" : "bg-white border-black/10"}`}
                            >
                                <div className="flex items-center gap-2 mb-6">
                                    {live ? (
                                        <span className="w-2 h-2 rounded-full bg-sn-green animate-pulse" />
                                    ) : (
                                        <Sparkles size={13} className="text-sn-blue" />
                                    )}
                                    <span
                                        className={`font-mono-e text-[10px] uppercase tracking-[0.22em] ${live ? "text-sn-green" : "text-sn-ink-2"}`}
                                    >
                                        {ph.tag}
                                    </span>
                                </div>
                                <h3 className="font-serif text-3xl text-sn-ink mb-6">
                                    {ph.title}
                                </h3>
                                <ul className="space-y-3">
                                    {(ph.items || []).map((it, j) => {
                                        const Icon = live ? CheckCircle2 : Circle;
                                        return (
                                            <li
                                                key={j}
                                                className="flex items-start gap-3 text-sn-ink-2 text-sm md:text-base"
                                            >
                                                <Icon
                                                    size={16}
                                                    className={`${live ? "text-sn-green" : "text-sn-blue/50"} mt-0.5 shrink-0`}
                                                    strokeWidth={1.8}
                                                />
                                                <span>{it}</span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
